import api from '../services/api';

const isActiveStaffStatus = (status) =>
  status === 1 ||
  status === true ||
  (typeof status === 'string' && status.toLowerCase() === 'active');

export async function fetchPatientsForStaff(practiceId, { providerId, caregiverId } = {}) {
  const params = { limit: 100 };
  if (providerId != null) params.providerId = providerId;
  if (caregiverId != null) params.caregiverId = caregiverId;

  const response = await api.get(`/practices/${practiceId}/patients`, { params });
  return response.data?.data?.patients || [];
}

function extractStaffList(response, key) {
  const raw =
    response.data?.data?.[key] ||
    response.data?.[key] ||
    response.data?.data ||
    response.data ||
    [];
  return Array.isArray(raw) ? raw : [];
}

export async function fetchActiveProviders(practiceId, excludeId) {
  const response = await api.get(`/practices/${practiceId}/providers`, { params: { limit: 1000 } });
  return extractStaffList(response, 'providers').filter((provider) => {
    return String(provider?.id) !== String(excludeId) && isActiveStaffStatus(provider?.status);
  });
}

export async function fetchActiveCaregivers(practiceId, excludeId) {
  const response = await api.get(`/practices/${practiceId}/caregivers`, { params: { limit: 1000 } });
  return extractStaffList(response, 'caregivers').filter((caregiver) => {
    return String(caregiver?.id) !== String(excludeId) && isActiveStaffStatus(caregiver?.status);
  });
}

export function buildEmptyReassignments(patients = []) {
  const initial = {};
  patients.forEach((patient) => {
    initial[patient.id] = '';
  });
  return initial;
}

export function buildReassignmentsForAll(patients = [], assigneeId = '') {
  const next = {};
  patients.forEach((patient) => {
    next[patient.id] = assigneeId;
  });
  return next;
}

export function hasUnassignedReassignments(patients = [], reassignments = {}) {
  return patients.some((patient) => {
    const value = reassignments?.[patient.id] ?? reassignments?.[String(patient.id)] ?? '';
    return !String(value || '').trim();
  });
}

export async function batchReassignPatientsToProvider(practiceId, reassignments = {}) {
  const payload = {
    reassignments: Object.entries(reassignments).map(([patientId, providerId]) => ({
      patientId,
      providerId
    }))
  };
  await api.post(`/practices/${practiceId}/patients/batch-reassign-provider`, payload);
}

export async function batchReassignPatientsToCaregiver(practiceId, reassignments = {}) {
  const payload = {
    reassignments: Object.entries(reassignments).map(([patientId, caregiverId]) => ({
      patientId,
      caregiverId
    }))
  };
  await api.post(`/practices/${practiceId}/patients/batch-reassign`, payload);
}

export async function fetchPatientsForSystemCaregiver(caregiverId) {
  const response = await api.get(`/system-caregivers/${caregiverId}/patients`, {
    params: { limit: 1000 }
  });
  return response.data?.data || [];
}

export async function fetchActiveSystemCaregivers(excludeId, practiceId) {
  const params = { limit: 1000, status: 'active' };
  if (practiceId != null && practiceId !== '') {
    params.practiceId = practiceId;
  }
  const response = await api.get('/system-caregivers', { params });
  const raw =
    response.data?.data?.caregivers ||
    response.data?.caregivers ||
    response.data?.data ||
    response.data ||
    [];
  const caregivers = Array.isArray(raw) ? raw : [];
  return caregivers.filter((caregiver) => {
    const status = String(caregiver?.status ?? '').trim().toLowerCase();
    const isActive = status === 'active' || status === 'a' || status === '2';
    return String(caregiver?.id) !== String(excludeId) && isActive;
  });
}

/**
 * Active system caregivers eligible for reassignment — scoped to the practice(s)
 * of the patients being reassigned (or an explicit practiceId fallback).
 */
export async function fetchActiveSystemCaregiversForReassignment(
  excludeId,
  { practiceId, patients = [] } = {}
) {
  const fromPatients = [
    ...new Set(
      (Array.isArray(patients) ? patients : [])
        .map((p) => p?.practice_id ?? p?.practiceId)
        .filter((id) => id != null && String(id).trim() !== '')
        .map((id) => String(id))
    )
  ];

  const practiceIds =
    fromPatients.length > 0
      ? fromPatients
      : practiceId != null && practiceId !== ''
        ? [String(practiceId)]
        : [];

  if (practiceIds.length === 0) {
    return fetchActiveSystemCaregivers(excludeId);
  }

  const lists = await Promise.all(
    practiceIds.map((pid) => fetchActiveSystemCaregivers(excludeId, pid))
  );
  const byId = new Map();
  lists.flat().forEach((caregiver) => {
    if (caregiver?.id != null) byId.set(String(caregiver.id), caregiver);
  });
  return [...byId.values()];
}

export async function batchReassignPatientsToSystemCaregiver(fromCaregiverId, reassignments = {}) {
  const payload = {
    reassignments: Object.entries(reassignments).map(([patientId, systemCaregiverId]) => ({
      patientId,
      systemCaregiverId
    }))
  };
  await api.post(`/system-caregivers/${fromCaregiverId}/batch-reassign-patients`, payload);
}

export function getSystemCaregiverPatientName(patient) {
  return (
    patient?.patient_name ||
    `${patient?.last_name || ''}, ${patient?.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() ||
    `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() ||
    'N/A'
  );
}

export function getProviderAssigneeName(patient) {
  return (
    patient?.provider_name ||
    patient?.assigned_to_name ||
    patient?.current_provider_name ||
    ((patient?.provider_first_name || patient?.provider_last_name)
      ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
      : '') ||
    'N/A'
  );
}

export function getCaregiverAssigneeName(patient) {
  return (
    patient?.caregiver_name ||
    patient?.assigned_to_name ||
    patient?.current_caregiver_name ||
    ((patient?.caregiver_first_name || patient?.caregiver_last_name)
      ? `${patient.caregiver_first_name || ''} ${patient.caregiver_last_name || ''}`.trim()
      : '') ||
    'N/A'
  );
}
